import { type RefObject, useEffect, useRef, useState, useCallback } from "react";
import type Three from "three"; // only import type so we cannot include anything directly so the bundle size doesn't grow
import getOrbitControls, { type OrbitControls as OrbitControlsType } from "../utils/classes/OrbitControls";
import { useCommonSelector } from "../../../common-deprecated/redux/commonStore";
import { useWindowDimensionsContext } from "../../../common-deprecated/containers/WindowDimensionsProvider";

type PositionType = { x: number; y: number; z: number };

// Lazy loads the custom build of three js from the assets folder
const useThreeJS = (load: boolean): typeof Three | null => {
    const path = useCommonSelector((state) => `${state.commonSettings.resourcePath}/js/three.min.js`);
    const [script, setScript] = useState<typeof Three | null>(null);

    useEffect(() => {
        if (load && !script) {
            // Keep using window.require over async import in the Build because this syntax breaks IE11 compatibility completely
            window.require([path], (threeJS) => {
                // check for the constructor we use later, so we know that the bundle successfully loaded
                // if the js cant be loaded/parsed it returns the script element that failed to load
                if (threeJS?.WebGLRenderer) setScript(threeJS);
            });
        }
    }, [load, path]);

    return script;
};

// helper to calculate linear smoothing animation for one coordinate, time and duration in ms
// Source: https://stackoverflow.com/questions/8316882/what-is-an-easing-function
const getPartialNewPositon = (
    newCoord: number,
    originalCoord: number,
    elapsedTime: number,
    duration: number,
): number => {
    return originalCoord + (newCoord - originalCoord) * (elapsedTime / duration);
};

type Use3DSpinType = {
    initialized: boolean;
    loading: boolean;
    dragged: boolean;
    controls?: OrbitControlsType;
    updateCanvasSize: () => void;
    updateTexture: (imageUrls: string[]) => void;
    updatePosition: (position: PositionType) => void;
    getPosition: () => PositionType | undefined;
};

// Inner workings for a WebGL scene with skybox
const useInteriorSpin3D = (
    canvas: RefObject<HTMLCanvasElement>,
    enabled: boolean,
    initialViewOverwrite?: { x: number; y: number; z: number; fov: number },
): Use3DSpinType => {
    const { width: viewportWidth } = useWindowDimensionsContext();
    const THREE = useThreeJS(enabled); // this is the dynamically loaded module
    const [dragged, setDragged] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [loading, setLoading] = useState(false);
    const enableRenderLoop = useRef(false);
    const movePositionAnimation = useRef<{ orgPos: PositionType; newPos: PositionType; startTimestamp: number } | null>(
        null,
    );

    // WebGL variables
    const renderer = useRef<THREE.WebGLRenderer>();
    const camera = useRef<THREE.PerspectiveCamera>();
    const controls = useRef<OrbitControlsType>();
    const scene = useRef<THREE.Scene>();
    const render = useRef<(time: number) => void>();

    // -------------------------------------------
    // Setup scene and controls
    // -------------------------------------------

    // checks if canvas has changed size, and updates its internal resolution to match the element's actual size
    const updateCanvasSize = useCallback(() => {
        if (!THREE || !canvas.current || !renderer.current || !camera.current) return;
        const width = canvas.current.clientWidth;
        const height = canvas.current.clientHeight;
        // threejs sets width and height attributes on canvas with setSize which match the internal renderers size
        // values also need to exceed 0 so resizing is triggered otherwise three.js gets stuck on 0
        const needResize =
            (canvas.current.width !== width || canvas.current.height !== height) && width > 0 && height > 0;
        const ppiChanged = renderer.current.pixelRatio !== Math.ceil(window.devicePixelRatio);
        if (ppiChanged) {
            renderer.current.setPixelRatio(Math.ceil(window.devicePixelRatio));
        }

        // will always be true on first render
        if (needResize) {
            renderer.current.setSize(width, height, false);
            camera.current.aspect = width / height;
            camera.current.updateProjectionMatrix();

            // first render
            if (!initialized) setInitialized(true);
            window.requestAnimationFrame(render.current!);
        }
    }, [initialized, THREE]);

    // change the current texture (images should be preloaded!)
    const updateTexture = useCallback(
        (imageUrls: string[]): void => {
            if (!THREE || !scene.current) return;
            // Load new texture and replace existing
            const loader = new THREE.CubeTextureLoader();
            setLoading(true);
            const texture = loader.load(imageUrls, () => {
                window.requestAnimationFrame(render.current!);
                setLoading(false);
            });
            texture.minFilter = THREE.LinearFilter;
            scene.current.background = texture;
        },
        [THREE],
    );

    const updatePosition = useCallback(
        (position: PositionType): void => {
            if (!THREE || !controls.current || !render.current) return;
            // the animation to move gradually to the new position happens in the render loop
            movePositionAnimation.current = {
                orgPos: { x: controls.current.target.x, y: controls.current.target.y, z: controls.current.target.z },
                newPos: position,
                startTimestamp: Date.now(),
            };
            enableRenderLoop.current = true;
            window.requestAnimationFrame(render.current);
        },
        [initialized, THREE],
    );

    const getPosition = useCallback((): PositionType | undefined => {
        if (!THREE || !controls.current || !render.current) return;
        const target = new THREE.Vector3();
        controls.current.object.getWorldDirection(target);
        return { x: target.x, y: target.y, z: target.z };
    }, [initialized, THREE]);

    useEffect(updateCanvasSize, [viewportWidth, updateCanvasSize]);

    // setup variables
    useEffect(() => {
        // only execute once per page load
        if (canvas.current && enabled && THREE && !controls.current) {
            // setup renderer and scene
            renderer.current = new THREE.WebGLRenderer({ canvas: canvas.current, alpha: true, antialias: true });
            renderer.current.autoClearColor = false;
            // set ppi for renderer
            renderer.current.setPixelRatio(Math.ceil(window.devicePixelRatio));
            scene.current = new THREE.Scene();

            // setup controls & camera
            camera.current = new THREE.PerspectiveCamera(initialViewOverwrite?.fov || 70); // fov set to 70 to reduce fisheye, see #8573
            const OrbitControls = getOrbitControls(THREE);
            controls.current = new OrbitControls(camera.current, canvas.current);
            if (!controls.current) return; // typing fix
            controls.current.target.set(
                initialViewOverwrite?.x || 0,
                initialViewOverwrite?.y || -1.1,
                initialViewOverwrite?.z || 3.86, // 3.86 is a magic number to compensate the 70 fov, see #8573
            ); // look toward left seats
            controls.current.enableDamping = true;
            controls.current.dampingFactor = 0.065; // manually fine-tuned for a 'good' feel
            controls.current.rotateSpeed = -0.17; // negative speed to invert mouse direction
            controls.current.enableZoom = false; // necessary for disabling scroll jacking
            controls.current.update();

            // stop rendering when user interacts after timeout so the damping animation can still complete
            const animationTimeout = 250; // linked to dampingFactor (magic value)
            let fadeoutAnimation: NodeJS.Timeout;

            // Render a new frame
            render.current = (): void => {
                if (!controls.current || !renderer.current || !scene.current || !camera.current) return;
                // gradually move towards the new position if set
                if (movePositionAnimation.current) {
                    const { orgPos, newPos, startTimestamp } = movePositionAnimation.current;
                    const now = Date.now();
                    const elapsedTime = now - startTimestamp;

                    controls.current.target.set(
                        getPartialNewPositon(newPos.x, orgPos.x, elapsedTime, animationTimeout),
                        getPartialNewPositon(newPos.y, orgPos.y, elapsedTime, animationTimeout),
                        getPartialNewPositon(newPos.z, orgPos.z, elapsedTime, animationTimeout),
                    );

                    // cleanup animation
                    if (elapsedTime >= animationTimeout) {
                        movePositionAnimation.current = null;
                        enableRenderLoop.current = false;
                    }
                }

                // update controls and rerender canvas
                controls.current.update();
                renderer.current.render(scene.current, camera.current);
                // loop still exists for smooth stopping animation
                if (enableRenderLoop.current) {
                    window.requestAnimationFrame(render.current!);
                }
            };

            // start render loop and reset animation timeout
            const onStart = (): void => {
                if (fadeoutAnimation) clearTimeout(fadeoutAnimation);
                enableRenderLoop.current = true;
                window.requestAnimationFrame(render.current!);
            };

            // stop render loop after timeout
            const onEnd = (): void => {
                fadeoutAnimation = setTimeout(() => {
                    enableRenderLoop.current = false;
                }, animationTimeout);
            };

            const onChange = (): void => {
                setDragged(true);
            };

            controls.current.addEventListener("start", onStart);
            controls.current.addEventListener("end", onEnd);
            controls.current.addEventListener("change", onChange);
            updateCanvasSize();
            // Cleaning up the eventlisteners breaks rendering after switching personalization tabs.
            // For some reason React cleans up this hook without the component unmounting.
        }
    }, [enabled, THREE, canvas]);

    return {
        initialized,
        loading,
        dragged,
        controls: controls.current,
        updateCanvasSize,
        updateTexture,
        updatePosition,
        getPosition,
    };
};

export default useInteriorSpin3D;
